import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDirtyForm } from '../contexts/DirtyFormContext';

const card = {
  border: '1px solid #cbd5e1',
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: '24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  background: '#ffffff',
};
const header = {
  padding: '14px 20px',
  background: '#f1f5f9',
  borderBottom: '1px solid #e2e8f0',
  fontSize: '1.02rem',
  fontWeight: 600,
  color: '#334155',
};
const body = { padding: '20px' };
const btn = {
  padding: '8px 14px',
  marginRight: '8px',
  marginBottom: '8px',
  cursor: 'pointer',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  background: '#fff',
};
const btnPrimary = { ...btn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' };
const btnDanger = { ...btn, background: '#b91c1c', color: '#fff', borderColor: '#b91c1c' };
const inputStyle = {
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  marginRight: '8px',
  marginBottom: '8px',
};
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.88rem',
  marginTop: '12px',
};
const th = {
  border: '1px solid #ddd',
  padding: '8px',
  background: '#f4f4f4',
  textAlign: 'left',
};
const td = { border: '1px solid #ddd', padding: '8px' };

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

const RESTORE_LOCK_MESSAGE = '복원이 진행 중입니다. 끝날 때까지 이 화면에 있어야 합니다.';

const RESTORE_STAGE_LABELS = {
  starting: '복원 준비',
  extract: '백업 파일 확인',
  'safety-backup': '안전 백업',
  'db-restore': '데이터베이스 복원',
  'schema-sync': '테이블 구조 맞춤',
  uploads: '업로드 파일 복원',
  rollback: '이전 상태로 되돌리는 중',
  running: '복원 진행',
  done: '복원 완료',
  failed: '복원 실패',
};

function RestoreModal({ backup, onClose, onConfirm, loading, progress }) {
  const [step, setStep] = useState(progress ? 3 : 1);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (progress) setStep(3);
  }, [progress]);

  if (!backup && !progress) return null;

  const locked = Boolean(progress && (progress.running || progress.stage === 'done'));
  const percent = Math.max(0, Math.min(100, Number(progress?.percent) || 0));
  const stageLabel = RESTORE_STAGE_LABELS[progress?.stage] || '복원 진행';

  const handleConfirm = () => {
    if (locked) return;
    if (step === 1) {
      setStep(2);
      return;
    }
    onConfirm(password);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={locked ? undefined : onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '520px',
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 16px', color: '#b91c1c' }}>
          {step === 3 ? '데이터 복원 진행' : '⚠️ 데이터 복원 경고'}
        </h3>

        {step === 1 && (
          <>
            <p style={{ lineHeight: 1.6, color: '#334155', margin: '0 0 12px' }}>
              선택한 백업 시점(<strong>{formatDate(backup.createdAt)}</strong>)의 모든 데이터로
              현재 데이터가 <strong>완전히 덮어씌워집니다.</strong>
            </p>
            <p style={{ lineHeight: 1.6, color: '#334155', margin: '0 0 12px' }}>
              사용자 정보, 근무기록, 연차, 급여, 업로드 파일 등 <strong>모든 정보</strong>가
              해당 시점으로 복원됩니다. 이 작업은 되돌릴 수 없습니다.
            </p>
            <p style={{ lineHeight: 1.6, color: '#64748b', fontSize: '0.9rem', margin: '0 0 20px' }}>
              복원 전 현재 상태가 자동으로 안전 백업됩니다. 복원이 시작되면 끝날 때까지 이 화면을 벗어날 수 없습니다.
            </p>
          </>
        )}

        {step === 2 && (
          <>
            <p style={{ lineHeight: 1.6, color: '#334155', margin: '0 0 12px' }}>
              복원을 실행하려면 <strong>현재 로그인한 관리자 계정의 비밀번호</strong>를 입력하세요.
            </p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && password && handleConfirm()}
            />
          </>
        )}

        {step === 3 && (
          <>
            <p style={{ lineHeight: 1.6, color: '#334155', margin: '0 0 8px' }}>
              {progress?.message || '복원을 진행하고 있습니다.'}
            </p>
            <p style={{ lineHeight: 1.6, color: '#64748b', fontSize: '0.9rem', margin: '0 0 16px' }}>
              {stageLabel} · {percent}%
            </p>
            <div
              style={{
                height: '12px',
                borderRadius: '999px',
                background: '#e2e8f0',
                overflow: 'hidden',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  width: `${percent}%`,
                  height: '100%',
                  background: progress?.stage === 'failed' ? '#b91c1c' : '#0f172a',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
            {progress?.stage !== 'failed' && (
              <p style={{ lineHeight: 1.6, color: '#b91c1c', fontSize: '0.9rem', margin: 0 }}>
                복원이 끝날 때까지 다른 메뉴로 이동하거나 창을 닫지 마세요.
              </p>
            )}
          </>
        )}

        {step !== 3 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <button type="button" style={btn} onClick={onClose} disabled={loading}>
              취소
            </button>
            {step === 1 ? (
              <button type="button" style={btnDanger} onClick={handleConfirm}>
                계속
              </button>
            ) : (
              <button
                type="button"
                style={btnDanger}
                onClick={handleConfirm}
                disabled={!password || loading}
              >
                {loading ? '복원 시작 중...' : '복원 실행'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminBackupPage() {
  const { logout } = useAuth();
  const { setNavigationLock } = useDirtyForm();
  const handledResultRef = useRef('');
  const jobIdRef = useRef(null);
  const [settings, setSettings] = useState({
    enabled: false,
    scheduleType: 'daily',
    hour: 2,
    minute: 0,
    dayOfWeek: 0,
    retentionCount: 14,
    lastRunAt: null,
    lastStatus: null,
    lastError: null,
  });
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupRunning, setBackupRunning] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get('/admin/backup/config');
      setSettings(res.data.settings || {});
      setBackups(res.data.backups || []);
      setBackupRunning(res.data.isBackupRunning || res.data.isRestoreRunning);
    } catch (err) {
      alert(err.response?.data?.message || '백업 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const finishRestoreSuccess = useCallback(() => {
    setNavigationLock(null);
    alert('복원이 완료되었습니다. 백업 당시 계정으로 다시 로그인하세요.');
    logout();
    sessionStorage.clear();
    window.location.href = '/login';
  }, [logout, setNavigationLock]);

  const applyRestoreStatus = useCallback((status) => {
    if (!status) return;
    if (status.running) {
      if (jobIdRef.current && status.jobId && status.jobId !== jobIdRef.current) return;
      if (status.jobId) jobIdRef.current = status.jobId;
      setRestoreProgress(status);
      setRestoreLoading(true);
      setNavigationLock(RESTORE_LOCK_MESSAGE);
      setRestoreTarget((current) => current || { id: status.backupId, createdAt: status.startedAt });
      return;
    }
    // 직전 복원의 완료 기록은 새 작업이 끝나기 전에 남아 있다. 이번 jobId 와 같을 때만 끝으로 본다.
    if (!jobIdRef.current || !status.jobId || status.jobId !== jobIdRef.current) return;
    if (status.stage === 'done' || status.relogin) {
      const resultKey = `${status.jobId}:done`;
      if (handledResultRef.current === resultKey) return;
      handledResultRef.current = resultKey;
      jobIdRef.current = null;
      setRestoreProgress({ ...status, percent: 100, stage: 'done' });
      finishRestoreSuccess();
      return;
    }
    if (status.stage === 'failed') {
      const resultKey = `${status.jobId}:failed`;
      if (handledResultRef.current === resultKey) return;
      handledResultRef.current = resultKey;
      jobIdRef.current = null;
      setRestoreProgress(null);
      setRestoreTarget(null);
      setRestoreLoading(false);
      setNavigationLock(null);
      alert(status.message || '복원에 실패했습니다.');
    }
  }, [finishRestoreSuccess, setNavigationLock]);

  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const res = await api.get('/admin/backup/status');
        if (!stopped) applyRestoreStatus(res.data);
      } catch {
        // 상태 조회 실패는 다음 주기에 다시 시도
      }
    };
    poll();
    const timer = setInterval(poll, 2000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [applyRestoreStatus]);

  const handleRunBackup = async () => {
    if (!window.confirm('지금 전체 데이터 백업을 실행할까요?\n(DB + 업로드 파일 포함)')) return;
    setBackupRunning(true);
    try {
      const res = await api.post('/admin/backup/run', null, { timeout: 30 * 60 * 1000 });
      alert(res.data.message || '백업이 완료되었습니다.');
      await loadData();
    } catch (err) {
      alert(err.response?.data?.message || '백업 실행에 실패했습니다.');
    } finally {
      setBackupRunning(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await api.put('/admin/backup/config', {
        enabled: settings.enabled,
        scheduleType: settings.scheduleType,
        hour: Number(settings.hour),
        minute: Number(settings.minute),
        dayOfWeek: Number(settings.dayOfWeek),
        retentionCount: 14,
      });
      setSettings(res.data.settings);
      alert(res.data.message || '설정이 저장되었습니다.');
    } catch (err) {
      alert(err.response?.data?.message || '설정 저장에 실패했습니다.');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDownload = async (backup) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/backup/download/${backup.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || '다운로드 실패');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backup.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || '다운로드에 실패했습니다.');
    }
  };

  const handleRestore = async (password) => {
    if (!restoreTarget) return;
    if (!window.confirm('정말 복원을 실행할까요? 모든 데이터가 해당 시점으로 교체됩니다.')) return;

    jobIdRef.current = null;
    setRestoreLoading(true);
    setNavigationLock(RESTORE_LOCK_MESSAGE);
    setRestoreProgress({
      running: true,
      stage: 'starting',
      percent: 5,
      message: '복원을 시작합니다.',
    });
    try {
      const res = await api.post('/admin/backup/restore', {
        backupId: restoreTarget.id,
        password,
        confirm: true,
      });
      if (res.data?.jobId) jobIdRef.current = res.data.jobId;
    } catch (err) {
      jobIdRef.current = null;
      setRestoreProgress(null);
      setRestoreLoading(false);
      setNavigationLock(null);
      alert(err.response?.data?.message || '복원에 실패했습니다.');
    }
  };

  if (loading) {
    return <p style={{ padding: '24px' }}>불러오는 중...</p>;
  }

  return (
    <div style={{ padding: '24px', maxWidth: '960px' }}>
      <h2 style={{ margin: '0 0 8px', color: '#0f172a' }}>데이터 백업 설정</h2>
      <p style={{ color: '#64748b', margin: '0 0 24px', fontSize: '0.95rem' }}>
        데이터베이스와 업로드 파일을 포함한 전체 데이터를 백업·복원합니다. 백업 파일은 최근 14개까지 보관됩니다.
      </p>

      {/* 즉시 백업 */}
      <div style={card}>
        <div style={header}>즉시 백업</div>
        <div style={body}>
          <p style={{ margin: '0 0 12px', color: '#475569', fontSize: '0.9rem' }}>
            PostgreSQL 데이터베이스 전체와 uploads 폴더를 하나의 압축 파일로 백업합니다.
          </p>
          {settings.lastRunAt && (
            <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#64748b' }}>
              마지막 백업: {formatDate(settings.lastRunAt)}
              {settings.lastStatus === 'success' && ' (성공)'}
              {settings.lastStatus === 'failed' && ` (실패: ${settings.lastError || ''})`}
            </p>
          )}
          <button
            type="button"
            style={btnPrimary}
            onClick={handleRunBackup}
            disabled={backupRunning}
          >
            {backupRunning ? '백업 실행 중...' : '지금 백업 실행'}
          </button>
        </div>
      </div>

      {/* 스케줄 백업 */}
      <div style={card}>
        <div style={header}>스케줄 백업</div>
        <div style={body}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))}
            />
            스케줄 백업 활성화
          </label>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ marginRight: '12px' }}>
              <input
                type="radio"
                name="scheduleType"
                value="daily"
                checked={settings.scheduleType === 'daily'}
                onChange={() => setSettings((s) => ({ ...s, scheduleType: 'daily' }))}
              />
              {' '}매일
            </label>
            <label>
              <input
                type="radio"
                name="scheduleType"
                value="weekly"
                checked={settings.scheduleType === 'weekly'}
                onChange={() => setSettings((s) => ({ ...s, scheduleType: 'weekly' }))}
              />
              {' '}매주
            </label>
          </div>

          {settings.scheduleType === 'weekly' && (
            <select
              value={settings.dayOfWeek}
              onChange={(e) => setSettings((s) => ({ ...s, dayOfWeek: Number(e.target.value) }))}
              style={inputStyle}
            >
              {DAY_LABELS.map((label, i) => (
                <option key={label} value={i}>
                  {label}요일
                </option>
              ))}
            </select>
          )}

          <select
            value={settings.hour}
            onChange={(e) => setSettings((s) => ({ ...s, hour: Number(e.target.value) }))}
            style={inputStyle}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {String(i).padStart(2, '0')}시
              </option>
            ))}
          </select>

          <select
            value={settings.minute}
            onChange={(e) => setSettings((s) => ({ ...s, minute: Number(e.target.value) }))}
            style={inputStyle}
          >
            {[0, 15, 30, 45].map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, '0')}분
              </option>
            ))}
          </select>

          <p style={{ margin: '12px 0', fontSize: '0.85rem', color: '#64748b' }}>
            백업 파일 보존: 최근 14개 (초과분 자동 삭제)
          </p>

          <button
            type="button"
            style={btnPrimary}
            onClick={handleSaveConfig}
            disabled={savingConfig}
          >
            {savingConfig ? '저장 중...' : '스케줄 설정 저장'}
          </button>
        </div>
      </div>

      {/* 백업 목록 */}
      <div style={card}>
        <div style={header}>백업 목록 ({backups.length}/14)</div>
        <div style={body}>
          {!backups.length ? (
            <p style={{ color: '#64748b', margin: 0 }}>백업 파일이 없습니다.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={th}>백업 시각</th>
                    <th style={th}>파일명</th>
                    <th style={th}>크기</th>
                    <th style={th}>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((b) => (
                    <tr key={b.id}>
                      <td style={td}>{formatDate(b.createdAt)}</td>
                      <td style={td}>{b.filename}</td>
                      <td style={td}>{formatBytes(b.size)}</td>
                      <td style={td}>
                        <button type="button" style={btn} onClick={() => handleDownload(b)}>
                          다운로드
                        </button>
                        <button
                          type="button"
                          style={btnDanger}
                          onClick={() => setRestoreTarget(b)}
                          disabled={backupRunning || restoreLoading}
                        >
                          복원
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(restoreTarget || restoreProgress) && (
        <RestoreModal
          backup={restoreTarget}
          onClose={() => {
            if (restoreLoading || restoreProgress?.running) return;
            setRestoreTarget(null);
            setRestoreProgress(null);
          }}
          onConfirm={handleRestore}
          loading={restoreLoading}
          progress={restoreProgress}
        />
      )}
    </div>
  );
}
